/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef } from 'react';
import { TextInputControlProps } from '../TextInputControl';
import controlHintIds from '../controlHintIds';
import FormControlWrapper from '../FormControlWrapper';
import useCharacterCount from '../useCharacterCount';
import MaskedInput, { MaskedInputProps } from './MaskedInput';

export type MaskedInputControlProps = TextInputControlProps & MaskedInputProps;

/**
 * The Masked Input Control Component based on [The USWDS Input Mask component](https://designsystem.digital.gov/components/input-mask/)
 */
const MaskedInputControl = forwardRef<HTMLInputElement, MaskedInputControlProps>(
  (
    {
      id,
      className,
      label,
      errorMessage,
      hint,
      labelHint,
      note,
      characterCountMax,
      onChange,
      value,
      variant,
      type = 'text',
      maxLength,
      'aria-describedby': ariaDescribedBy,
      required,
      requiredLabel,
      ...rest
    }: MaskedInputControlProps,
    ref,
  ): JSX.Element => {
    const [characterCount, changeHandler] = useCharacterCount<HTMLInputElement>(value, characterCountMax, onChange);
    const { labelHintId, hintId, errorId, characterCountId, describedBy } = controlHintIds(
      id,
      labelHint,
      hint,
      errorMessage,
      characterCountMax,
      ariaDescribedBy,
    );
    return (
      <>
        <FormControlWrapper
          id={id}
          label={label}
          labelHint={labelHint}
          labelHintId={labelHintId}
          hint={hint}
          hintId={hintId}
          note={note}
          errorId={errorId}
          errorMessage={errorMessage}
          characterCountMax={characterCountMax}
          characterCount={characterCount}
          characterCountId={characterCountId}
          required={required}
          requiredLabel={requiredLabel}
          ungroup
        >
          <MaskedInput
            {...rest}
            id={id}
            aria-describedby={describedBy}
            type={type}
            className="masked-input__input"
            ref={ref}
            value={value}
            errorId={errorId}
            variant={variant}
            maxLength={characterCountMax ? undefined : maxLength}
            onChange={changeHandler}
            aria-required={required}
            required={required}
          />
        </FormControlWrapper>
      </>
    );
  },
);

export default MaskedInputControl;
